/* ==========================================================================
   Cart drawer upsell
   --------------------------------------------------------------------------
   - Single-product accordion rendered above the checkout button
   - Hero images are fixed from theme settings; variant change updates pricing only
   - Add-to-cart always adds the item as a pair (2 panels), using the same
     properties as the PDP pair flow:
         properties[Sold As]     = "Pair (2 Panels)"
         properties[_units_per]  = "2"
         properties[_2pack]      = "2Pack"
   - After add, refreshes cart drawer sections so line items + totals update.
   ========================================================================== */

(function () {
  'use strict';

  if (window.customElements && customElements.get('cart-upsell')) return;

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.theme_money_format) {
      return Shopify.formatMoney(cents, window.theme_money_format);
    }
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.moneyFormat) {
      return Shopify.formatMoney(cents, window.moneyFormat);
    }
    // Fallback: simple $X.XX
    var value = (Math.round(cents) / 100).toFixed(2);
    return '$' + value;
  }

  function safeParseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      console.warn('[cart-upsell] Invalid JSON:', e);
      return null;
    }
  }

  function formatFixedDollars(amount) {
    const value = Number(amount);
    if (!Number.isFinite(value)) return '$0.00';
    return '$' + value.toFixed(2);
  }
  

  function applyPercentDiscountCents(amountCents, percent) {
    const amount = Number(amountCents) || 0;
    const pct = Number(percent) || 0;
    if (pct <= 0) return amount;
    const discountCents = Math.ceil((amount * pct) / 100);
    return Math.max(0, amount - discountCents);
  }

  const UPSELL_FREE_THRESHOLD_CENTS = 10000;
  let upsellThresholdSyncInFlight = false;
  let thresholdIndicatorShownAt = 0;

  function parseUpsellPriceValue(raw) {
    const parsed = parseFloat(String(raw == null ? '' : raw).trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  function normalizeProductType(rawType) {
    return String(rawType || '').trim().toLowerCase();
  }

  function normalizeTags(rawTags) {
    return String(rawTags || '')
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
  }

  function buildLineMetadataByVariant() {
    const metadata = {};
    const rows = document.querySelectorAll('#CartDrawer-CartItems .cart-item[data-variant-id]');
    rows.forEach((row) => {
      const variantId = String(row.getAttribute('data-variant-id') || '').trim();
      if (!variantId) return;
      metadata[variantId] = {
        tags: normalizeTags(row.getAttribute('data-product-tags')),
      };
    });
    return metadata;
  }

  function isRestrictedProduct(item, fallbackTags) {
    const itemType = normalizeProductType(item && (item.product_type || item.type));
    if (itemType === 'custom curtain' || itemType === 'roman shade') {
      return true;
    }
    if (Array.isArray(fallbackTags) && fallbackTags.indexOf('dropship') !== -1) {
      return true;
    }
    return false;
  }

  function isSwatchProduct(item) {
    const itemType = normalizeProductType(item && (item.product_type || item.type));
    return itemType === 'swatch' || itemType === 'swatches';
  }

  function getEligibleThresholdSubtotalCents(cartItems) {
    const items = Array.isArray(cartItems) ? cartItems : [];
    const lineMetadataByVariant = buildLineMetadataByVariant();
    let totalCents = 0;
    let hasNonRestrictedNonSwatch = false;
    let hasRestricted = false;

    items.forEach((item) => {
      if (item && item.properties && item.properties._upsell_price != null) return;

      const lineTotal = Number(item && item.final_line_price) || 0;
      totalCents += lineTotal;

      const variantKey = String(item && item.variant_id != null ? item.variant_id : '');
      const metadata = lineMetadataByVariant[variantKey] || {};
      const restricted = isRestrictedProduct(item, metadata.tags || []);
      const swatch = isSwatchProduct(item);
      if (restricted) {
        hasRestricted = true;
      } else if (!swatch) {
        hasNonRestrictedNonSwatch = true;
      }
    });

    if (hasNonRestrictedNonSwatch) {
      return Math.max(0, totalCents);
    }
    if (!hasRestricted) {
      return Math.max(0, totalCents);
    }
    return 0;
  }

  function getSectionsContext() {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');
    const cart = cartDrawer || cartNotification;
    const sectionsToRender = cart && typeof cart.getSectionsToRender === 'function'
      ? cart.getSectionsToRender()
      : [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
    const sectionIds = sectionsToRender.map((s) => s.id);
    return { cart, sectionIds };
  }

  function setThresholdSyncIndicator(visible) {
    const cartDrawerRoot = document.getElementById('CartDrawer');
    if (!cartDrawerRoot) return;

    let indicator = document.getElementById('upsell-threshold-sync-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.id = 'upsell-threshold-sync-indicator';
      indicator.setAttribute('aria-live', 'polite');
      indicator.style.cssText = [
        'position:absolute',
        'left:50%',
        'top:64px',
        'transform:translateX(-50%)',
        'min-width:240px',
        'max-width:calc(100% - 24px)',
        'z-index:10001',
        'display:none',
        'align-items:center',
        'gap:10px',
        'background:rgba(0,0,0,0.82)',
        'color:#fff',
        'padding:10px 12px',
        'border-radius:8px',
        'font-size:12px',
      ].join(';');
      indicator.innerHTML = '<span style="width:14px;height:14px;border:2px solid #fff;border-top-color:transparent;border-radius:50%;display:inline-block;animation:upsell-threshold-spin .7s linear infinite;"></span><span>Updating free gift pricing...</span>';
      cartDrawerRoot.appendChild(indicator);

      if (!document.getElementById('upsell-threshold-sync-style')) {
        const style = document.createElement('style');
        style.id = 'upsell-threshold-sync-style';
        style.textContent = '@keyframes upsell-threshold-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}';
        document.head.appendChild(style);
      }
    }

    if (visible) {
      thresholdIndicatorShownAt = Date.now();
      console.warn('[cart-upsell] Showing threshold sync loader');
    }
    indicator.style.display = visible ? 'flex' : 'none';
  }

  async function maybeSyncUpsellPriceByThreshold() {
    if (upsellThresholdSyncInFlight) return;
    upsellThresholdSyncInFlight = true;

    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cartData = await cartRes.json();
      if (!cartData || !Array.isArray(cartData.items)) {
        return;
      }
      const updatableUpsellItems = cartData.items.filter((item) => {
        return item && item.properties && item.properties._upsell_price != null;
      });
      const eligibleThresholdSubtotalCents = getEligibleThresholdSubtotalCents(cartData.items);
      const shouldBeFree = eligibleThresholdSubtotalCents >= UPSELL_FREE_THRESHOLD_CENTS;
      if (!updatableUpsellItems.length) return;

      const { cart, sectionIds } = getSectionsContext();
      let latestResponseData = null;
      const pendingUpdates = [];

      for (const item of updatableUpsellItems) {
        const currentPrice = parseUpsellPriceValue(item.properties._upsell_price);
        const basePrice = parseUpsellPriceValue(item.properties._upsell_base_price);
        let desiredPrice = shouldBeFree ? 0 : basePrice;
        const desiredQuantity = shouldBeFree ? 1 : Number(item.quantity) || 1;

        // Backward compatibility for older lines that don't have _upsell_base_price.
        if (!shouldBeFree && (desiredPrice === null || desiredPrice < 0)) {
          if (currentPrice !== null && currentPrice > 0) desiredPrice = currentPrice;
        }
        const quantityNeedsUpdate = Number(item.quantity) !== desiredQuantity;
        if (desiredPrice === null || desiredPrice < 0 || (currentPrice === desiredPrice && !quantityNeedsUpdate)) {
          continue;
        }
        pendingUpdates.push({ item, desiredPrice, desiredQuantity });
      }

      if (!pendingUpdates.length) return;
      setThresholdSyncIndicator(true);

      for (const update of pendingUpdates) {
        const item = update.item;
        const nextProperties = Object.assign({}, item.properties || {}, {
          _upsell_price: update.desiredPrice.toFixed(2),
        });
        const formData = new FormData();
        formData.append('id', String(item.key));
        formData.append('quantity', String(update.desiredQuantity));
        Object.keys(nextProperties).forEach((key) => {
          formData.append(`properties[${key}]`, String(nextProperties[key]));
        });
        sectionIds.forEach((id) => formData.append('sections[]', id));
        formData.append('sections_url', window.location.pathname);

        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        latestResponseData = await res.json();
      }

      if (latestResponseData && cart && typeof cart.renderContents === 'function' && latestResponseData.sections) {
        cart.renderContents(latestResponseData);
      }
    } catch (error) {
      console.error('[cart-upsell] threshold free sync failed:', error);
    } finally {
      const elapsed = Date.now() - thresholdIndicatorShownAt;
      const minVisibleMs = 700;
      const hideDelay = elapsed >= minVisibleMs ? 0 : (minVisibleMs - elapsed);
      setTimeout(() => {
        setThresholdSyncIndicator(false);
      }, hideDelay);
      upsellThresholdSyncInFlight = false;
    }
  }

  class CartUpsell extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.variants = safeParseJSON(this.querySelector('[data-cart-upsell-variants]')) || [];
      this.debugConfig = safeParseJSON(this.querySelector('[data-cart-upsell-debug-config]')) || null;
      this.isMilestoneUnlocked = this.dataset.milestoneUnlocked === 'true';
      this.saleDiscountPct = parseFloat(this.dataset.saleDiscountPct || '0') || 0;
      this.isInitiallyFree = this.dataset.isFree === 'true';
      this.freeLabel = 'FREE';

      this.toggleBtn = this.querySelector('[data-cart-upsell-toggle]');
      this.body = this.querySelector('[data-cart-upsell-body]');
      this.captionEl = this.querySelector('[data-cart-upsell-variant-caption]');
      this.select = this.querySelector('[data-cart-upsell-variant-select]');
      this.qtyWrap = this.querySelector('[data-cart-upsell-qty-wrap]');
      this.qtyInput = this.querySelector('[data-cart-upsell-qty]');
      this.qtyMinus = this.querySelector('[data-cart-upsell-qty-minus]');
      this.qtyPlus = this.querySelector('[data-cart-upsell-qty-plus]');
      this.atcBtn = this.querySelector('[data-cart-upsell-atc]');
      this.atcLabel = this.querySelector('[data-cart-upsell-atc-label]');
      this.errorEl = this.querySelector('[data-cart-upsell-error]');

      this.bannerPriceEl = this.querySelector('[data-cart-upsell-banner-price]');
      this.bannerRetailEl = this.querySelector('[data-cart-upsell-banner-retail]');
      this.priceEl = this.querySelector('[data-cart-upsell-price]');
      this.retailEl = this.querySelector('[data-cart-upsell-retail]');
      this.saleEl = this.querySelector('[data-cart-upsell-sale]');
      this.saleWrap = this.querySelector('[data-cart-upsell-sale-wrap]');
      this.saveWrap = this.querySelector('[data-cart-upsell-save-wrap]');
      this.saveEl = this.querySelector('[data-cart-upsell-save]');
      this.footnoteEl = this.querySelector('[data-cart-upsell-footnote]');

      this.atcDefaultLabel = (this.atcBtn && this.atcBtn.dataset.defaultLabel) || 'ADD TO CART';
      this.atcSelectLabel = (this.atcBtn && this.atcBtn.dataset.selectLabel)
        || (this.atcLabel ? this.atcLabel.textContent.trim() : 'SELECT A SIZE');
      this.atcFreeLabel = (this.atcBtn && this.atcBtn.dataset.freeLabel) || 'CLAIM YOUR FREE PAIR';


      this.logDebugBootstrap();

      this.bindEvents();
      this.initializeState();
    }

    logDebugBootstrap() {
      if (!this.debugConfig) {
        console.warn('[cart-upsell] Missing debug config JSON');
        return;
      }

      const activeSlots = (this.debugConfig.configured_slots || []).filter((slot) => {
        return slot && typeof slot.match === 'string' && slot.match.trim() !== '';
      });
      const optionCount = this.select ? this.select.options.length : 0;

      console.warn('[cart-upsell] Mounted', {
        product: this.debugConfig.product_handle,
        activeSlotCount: activeSlots.length,
        matchedVariantCount: this.variants.length,
        renderedOptionCount: optionCount,
      });

      console.groupCollapsed('[cart-upsell] Debug bootstrap');
      console.log('Product:', this.debugConfig.product_handle);
      console.log('Configured slots:', this.debugConfig.configured_slots || []);
      console.log('Active slots:', activeSlots);
      console.log('All product variants:', this.debugConfig.all_variants || []);
      console.log('Matched variants JSON:', this.variants);
      console.log('Rendered select options count:', optionCount);
      if (activeSlots.length > 0 && this.variants.length === 0) {
        console.warn('[cart-upsell] Slots configured but no variant matched. Check exact ID/title/option1 match values.');
      }
      console.groupEnd();
    }

    initializeState() {
      if (!this.select) return;

      if (!this.select.options.length) {
        console.warn('[cart-upsell] No variants rendered in select. Check configured match values in theme settings.');
      }

      // If merchant/product state ever renders without a selected option,
      // auto-pick the first enabled variant so UI + pricing stay in sync.
      if (!this.select.value) {
        const firstEnabledOption = Array.from(this.select.options).find((opt) => opt.value && !opt.disabled);
        if (firstEnabledOption) {
          this.select.value = firstEnabledOption.value;
        }
      }

      if (this.select.value) {
        this.onVariantChange();
      } else {
        this.setAtcState({ disabled: true, label: this.atcSelectLabel });
      }
    }

    bindEvents() {
      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => this.toggleAccordion());
      }

      if (this.select) {
        this.select.addEventListener('change', () => this.onVariantChange());
      }

      if (this.qtyMinus) {
        this.qtyMinus.addEventListener('click', () => this.updateQty(-1));
      }
      if (this.qtyPlus) {
        this.qtyPlus.addEventListener('click', () => this.updateQty(1));
      }
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => this.sanitizeQty());
      }

      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.addToCart();
        });
      }
    }

    toggleAccordion(forceOpen) {
      const isOpen = this.toggleBtn.getAttribute('aria-expanded') === 'true';
      const next = typeof forceOpen === 'boolean' ? forceOpen : !isOpen;
      this.toggleBtn.setAttribute('aria-expanded', next ? 'true' : 'false');
      if (next) {
        this.body.hidden = false;
      } else {
        this.body.hidden = true;
      }
    }

    updateQty(delta) {
      if (!this.qtyInput) return;
      const current = parseInt(this.qtyInput.value, 10) || 1;
      const next = Math.max(1, current + delta);
      this.qtyInput.value = next;
    }

    sanitizeQty() {
      if (!this.qtyInput) return;
      let v = parseInt(this.qtyInput.value, 10);
      if (isNaN(v) || v < 1) v = 1;
      this.qtyInput.value = v;
    }

    getSelectedVariant() {
      if (!this.select || !this.select.value) return null;
      const id = String(this.select.value);
      return this.variants.find((v) => String(v.id) === id) || null;
    }


    computePricing(variant) {
      if (!variant) return null;

      const basePrice = Number(variant.price) || 0;
      const baseCompare = Number(variant.compare_at_price) || 0;
      

      const manualPrice = parseFloat(variant.manual_price);
      const hasManualPrice = Number.isFinite(manualPrice) && manualPrice > 0;
      const salePriceRaw = basePrice * 2;
      const salePrice = applyPercentDiscountCents(salePriceRaw, this.saleDiscountPct);
      const retailPrice = baseCompare > 0 ? baseCompare * 2 : 0;
      const exclusivePrice = hasManualPrice
        ? Math.round(manualPrice * 100)
        : salePrice;

      const retailForSave = retailPrice > 0 ? retailPrice : salePrice;
      const saveAmount = Math.max(0, retailForSave - exclusivePrice);

      return {
        salePair: salePrice,
        retailPair: Math.round(retailPrice),
        exclusivePair: exclusivePrice,
        hasManualPrice: hasManualPrice,
        manualPriceValue: hasManualPrice ? manualPrice : null,
        saveAmount: Math.round(saveAmount),
      };
    }

    onVariantChange() {
      const variant = this.getSelectedVariant();

      if (!variant) {
        console.warn('[cart-upsell] Variant change triggered but no selected variant found', {
          selectValue: this.select ? this.select.value : null,
          variants: this.variants,
        });
        this.setAtcState({ disabled: true, label: this.atcSelectLabel });
        return;
      }

      // Update variant caption
      if (this.captionEl) {
        this.captionEl.textContent = (variant.option1 || variant.title || '').toUpperCase();
      }

      // Update pricing
      const p = this.computePricing(variant);
      if (p) {
        console.log('[cart-upsell] Variant pricing update', {
          variantId: variant.id,
          variantTitle: variant.title,
          manualPrice: variant.manual_price,
          computed: p,
        });
        const isFreeState = this.isMilestoneUnlocked || p.exclusivePair <= 0;
        this.isInitiallyFree = isFreeState;
        this.dataset.isFree = isFreeState ? 'true' : 'false';

        if (isFreeState) {
          if (this.qtyWrap) this.qtyWrap.hidden = true;
          if (this.footnoteEl) this.footnoteEl.hidden = true;
          if (this.bannerPriceEl) this.bannerPriceEl.textContent = this.freeLabel;
          if (this.bannerRetailEl) {
            this.bannerRetailEl.textContent = p.retailPair > 0 ? formatMoney(p.retailPair) : formatMoney(p.salePair);
          }
          if (this.priceEl) this.priceEl.textContent = this.freeLabel;
          if (this.retailEl) {
            this.retailEl.textContent = p.retailPair > 0 ? formatMoney(p.retailPair) : formatMoney(p.salePair);
          }
          if (this.saleEl) this.saleEl.textContent = formatMoney(p.salePair);
          if (this.saleWrap) this.saleWrap.hidden = true;
          if (this.saveWrap && this.saveEl) {
            const fullRetailSave = p.retailPair > 0 ? p.retailPair : p.salePair;
            this.saveWrap.hidden = fullRetailSave <= 0;
            this.saveEl.textContent = fullRetailSave > 0 ? formatMoney(fullRetailSave) : '';
          }
        } else {
        if (this.qtyWrap) this.qtyWrap.hidden = false;
        if (this.footnoteEl) this.footnoteEl.hidden = false;
        if (this.bannerPriceEl) {
          this.bannerPriceEl.textContent = p.hasManualPrice
            ? formatFixedDollars(p.manualPriceValue)
            : formatMoney(p.exclusivePair);
        }
        if (this.bannerRetailEl) {
          this.bannerRetailEl.textContent = p.retailPair > 0 ? formatMoney(p.retailPair) : '';
        }
        if (this.priceEl) {
          this.priceEl.textContent = p.hasManualPrice
            ? formatFixedDollars(p.manualPriceValue)
            : formatMoney(p.exclusivePair);
        }
        if (this.retailEl) {
          this.retailEl.textContent = p.retailPair > 0 ? formatMoney(p.retailPair) : '';
        }
        if (this.saleEl) this.saleEl.textContent = formatMoney(p.salePair);
        if (this.saleWrap) this.saleWrap.hidden = false;
        if (this.saveWrap && this.saveEl) {
          if (p.saveAmount > 0) {
            this.saveWrap.hidden = false;
            this.saveEl.textContent = formatMoney(p.saveAmount);
          } else {
            this.saveWrap.hidden = true;
            this.saveEl.textContent = '';
          }
        }
        }
      }

      // Enable ATC
      if (variant.available) {
        this.setAtcState({ disabled: false, label: this.getDefaultAtcLabel() });
      } else {
        this.setAtcState({ disabled: true, label: 'SOLD OUT' });
      }
    }

    getDefaultAtcLabel() {
      if (this.isMilestoneUnlocked || this.isInitiallyFree) {
        return this.atcFreeLabel || 'CLAIM YOUR FREE PAIR';
      }
      return this.atcDefaultLabel || 'ADD TO CART';
    }

    setAtcState({ disabled, label }) {
      if (!this.atcBtn) return;
      if (disabled) {
        this.atcBtn.setAttribute('disabled', 'disabled');
        this.atcBtn.setAttribute('aria-disabled', 'true');
      } else {
        this.atcBtn.removeAttribute('disabled');
        this.atcBtn.setAttribute('aria-disabled', 'false');
      }
      if (this.atcLabel && typeof label === 'string') {
        this.atcLabel.textContent = label;
      }
    }

    showError(msg) {
      if (!this.errorEl) return;
      if (msg) {
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      } else {
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    }

    async addToCart() {
      const variant = this.getSelectedVariant();
      if (!variant || !variant.available) return;

      this.showError(null);
      this.atcBtn.classList.add('loading');
      this.atcBtn.setAttribute('aria-disabled', 'true');
      this.atcBtn.setAttribute('disabled', 'disabled');

      this.sanitizeQty();
      const qty = parseInt(this.qtyInput.value, 10) || 1;
      const pricing = this.computePricing(variant);
      const basePerUnit = ((pricing ? pricing.exclusivePair : 0) / 100).toFixed(2);
      const isFreeState = !!(pricing && (this.isMilestoneUnlocked || pricing.exclusivePair <= 0));
      const transformPerUnit = isFreeState
        ? '0.00'
        : basePerUnit;

      // Determine sections to refresh (cart drawer + cart icon bubble)
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cart = cartDrawer || cartNotification;

      const sectionsToRender = cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender()
        : [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
      const sectionIds = sectionsToRender.map((s) => s.id);

      const formData = new FormData();
      formData.append('id', String(variant.id));
      formData.append('quantity', String(qty));
      formData.append('properties[Sold As]', 'Pair (2 Panels)');
      formData.append('properties[_units_per]', '2');
      formData.append('properties[_2pack]', '2Pack');
      // Single field for Cart Transform pricing
      formData.append('properties[_upsell_price]', transformPerUnit);
      formData.append('properties[_upsell_base_price]', basePerUnit);
      sectionIds.forEach((id) => formData.append('sections[]', id));
      formData.append('sections_url', window.location.pathname);

      const config = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      };

      try {
        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
        const res = await fetch(addUrl, config);
        let data = {};
        try {
          data = await res.json();
        } catch (e) {
          data = {};
        }

        if (!res.ok || data.status) {
          const msg = data.description || data.message || 'Could not add item to cart.';
          this.showError(msg);
          return;
        }

        // Publish cart update event (same pattern as product-form.js)
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-upsell',
              productVariantId: variant.id,
              cartData: data,
            });
          } catch (e) { /* noop */ }
        }

        // Re-render cart sections (cart-drawer + icon bubble)
        if (cart && typeof cart.renderContents === 'function' && data.sections) {
          cart.renderContents(data);
        } else {
          // Fallback: reload page
          window.location.reload();
        }
      } catch (err) {
        console.error('[cart-upsell] add-to-cart error:', err);
        this.showError('Something went wrong. Please try again.');
      } finally {
        if (this.atcBtn) {
          this.atcBtn.classList.remove('loading');
          // Re-evaluate enabled state
          this.onVariantChange();
        }
      }
    }
  }

  customElements.define('cart-upsell', CartUpsell);

  maybeSyncUpsellPriceByThreshold();
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      maybeSyncUpsellPriceByThreshold();
    });
    if (PUB_SUB_EVENTS.cartUpdateInMiniCart) {
      subscribe(PUB_SUB_EVENTS.cartUpdateInMiniCart, () => {
        maybeSyncUpsellPriceByThreshold();
      });
    }
  }
})();

